import { useMemo, useState, useEffect } from 'react';
import Validation from '~/util/Validation';
import { TEXT } from '../../constants';
import type { UpdateSettingsState } from '../../reducers/settingsReducer';
import hasValue from '~/util/hasValue';

type Args = {
  updateSettings: UpdateSettingsState;
};

type ValidationErrors = {
  leadscore: Array<string>;
  locations: Array<string>;
  phoneNumber: Array<string>;
  flynck: Array<string>;
};

export type TouchedFields = {
  leadscore: boolean;
  flynck: boolean;
  locations: boolean;
  phoneNumber: boolean;
};

type ReturnType = {
  errors: ValidationErrors;
  hasErrors: boolean;
  setFieldTouched: (field: keyof TouchedFields) => void;
  clearAllErrors: () => void;
};

const useSettingsValidation = ({ updateSettings }: Args): ReturnType => {
  const [errors, setErrors] = useState<ValidationErrors>({
    leadscore: [],
    locations: [],
    phoneNumber: [],
    flynck: [],
  });

  const [touchedFields, setTouchedFields] = useState<TouchedFields>({
    leadscore: false,
    flynck: false,
    locations: false,
    phoneNumber: false,
  });

  const validationErrors = useMemo((): ValidationErrors => {
    const leadscoreErrors: Array<string> = [];
    const locationsErrors: Array<string> = [];
    const phoneNumberErrors: Array<string> = [];
    const flynckErrors: Array<string> = [];

    // Leadscore validation
    if (touchedFields.leadscore) {
      const leadscore = updateSettings.rules.minLeadscore;
      const numberValue = Number(leadscore);

      if (!hasValue(leadscore) || !Validation.Number.isNumber(numberValue)) {
        leadscoreErrors.push(TEXT.invalidLeadscoreNumber);
      }
      if (numberValue <= 0 || numberValue > 100) {
        leadscoreErrors.push(TEXT.invalidMinLeadscore);
      }
    }

    // Flynck validation
    if (touchedFields.flynck) {
      const flynck = updateSettings.flynck;
      const missingField =
        !hasValue(flynck?.importSetupID) || !hasValue(flynck?.contextID);

      if (missingField) {
        flynckErrors.push(TEXT.flynkSettingsErrors);
      }
    }

    // Locations validation
    if (touchedFields.locations) {
      const services = updateSettings.services;
      const locationsSelectedForEachService = services.every(service =>
        service.locations.some(location => location.enabled),
      );

      if (!locationsSelectedForEachService) {
        locationsErrors.push(TEXT.noLocationsSelectedError);
      }
    }

    // Phone number validation
    if (touchedFields.phoneNumber) {
      const callbackPhoneNumber = updateSettings.callbackPhoneNumber;

      if (!hasValue(callbackPhoneNumber)) {
        phoneNumberErrors.push(TEXT.invalidPhoneNumber);
      }
    }

    return {
      leadscore: leadscoreErrors,
      locations: locationsErrors,
      phoneNumber: phoneNumberErrors,
      flynck: flynckErrors,
    };
  }, [updateSettings, touchedFields]);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  const hasErrors = useMemo(
    () => Object.values(errors).some(errorArray => errorArray.length > 0),
    [errors],
  );

  const setFieldTouched = (field: keyof TouchedFields) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true,
    }));
  };

  const clearAllErrors = () => {
    setErrors({
      leadscore: [],
      locations: [],
      phoneNumber: [],
      flynck: [],
    });

    setTouchedFields({
      leadscore: false,
      flynck: false,
      locations: false,
      phoneNumber: false,
    });
  };

  return { errors, hasErrors, setFieldTouched, clearAllErrors };
};

export default useSettingsValidation;
